<template>
  <div class="upload-images-container">
    <b-form class="modern-form">
      <b-form-group
        label="Comentario"
        label-for="caption-input"
        class="form-group-modern"
      >
        <b-form-textarea
          id="caption-input"
          v-model="caption"
          :rows="4"
          placeholder="Escribe un comentario sobre las imágenes..."
          class="modern-input"
        />
      </b-form-group>

      <b-form-group
        label="Imágenes"
        label-for="images-input"
        class="form-group-modern"
      >
        <div class="upload-area">
          <input
            id="images-input"
            ref="fileInput"
            type="file"
            multiple
            accept="image/*"
            class="file-input"
            @change="handleFileSelect"
          >
          <div
            v-if="images.length === 0"
            class="upload-placeholder"
            @click="triggerFileInput"
          >
            <i class="fa fa-cloud-upload fa-3x mb-3" />
            <p class="upload-text">
              Haz clic para seleccionar imágenes
            </p>
            <p class="upload-hint">
              Puedes seleccionar múltiples imágenes
            </p>
          </div>
          <div
            v-else
            class="images-preview"
          >
            <div
              v-for="(image, index) in images"
              :key="index"
              class="image-preview-item"
            >
              <img
                :src="image.preview"
                alt="Preview"
                class="preview-image"
              >
              <button
                type="button"
                class="remove-image-btn"
                @click="removeImage(index)"
              >
                <i class="fa fa-times" />
              </button>
            </div>
            <div
              class="add-more-btn"
              @click="triggerFileInput"
            >
              <i class="fa fa-plus fa-2x" />
              <p>Agregar más</p>
            </div>
          </div>
        </div>
      </b-form-group>

      <div
        v-if="errorMessage"
        class="error-message"
      >
        <i class="fa fa-exclamation-circle mr-2" />
        {{ errorMessage }}
      </div>
    </b-form>
  </div>
</template>

<script>
import { ref, watch } from '@vue/composition-api'

export default {
  name: 'UploadImages',
  props: {
    value: {
      type: Object,
      default: () => ({
        caption: '',
        images: []
      })
    }
  },
  setup (props, context) {
    const fileInput = ref(null)
    const caption = ref(props.value ? props.value.caption : '')
    const images = ref([])
    const errorMessage = ref('')

    if (props.value && props.value.images && Array.isArray(props.value.images) && props.value.images.length > 0) {
      if (typeof props.value.images[0] === 'string') {
        images.value = props.value.images.map(base64 => ({
          preview: base64,
          base64: base64
        }))
      } else {
        images.value = props.value.images
      }
    }

    let captionDebounceTimer = null
    let imagesDebounceTimer = null

    const emitUpdate = (immediate = false) => {
      const doEmit = () => {
        const imageData = images.value.map(img => img.base64 || img)
        context.emit('input', {
          caption: caption.value,
          images: imageData
        })
      }

      if (immediate) {
        doEmit()
      } else {
        context.root.$nextTick(doEmit)
      }
    }

    watch(caption, () => {
      if (captionDebounceTimer) clearTimeout(captionDebounceTimer)
      captionDebounceTimer = setTimeout(() => emitUpdate(), 300)
    })

    watch(() => images.value.length, () => {
      if (imagesDebounceTimer) clearTimeout(imagesDebounceTimer)
      imagesDebounceTimer = setTimeout(() => emitUpdate(true), 100)
    })

    const triggerFileInput = () => {
      if (fileInput.value) fileInput.value.click()
    }

    const handleFileSelect = (event) => {
      errorMessage.value = ''
      const files = Array.from(event.target.files || [])

      if (files.length === 0) return

      const maxSize = 10 * 1024 * 1024
      const validFiles = []

      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          errorMessage.value = 'Solo se permiten archivos de imagen'
          continue
        }
        if (file.size > maxSize) {
          errorMessage.value = `La imagen ${file.name} es demasiado grande. Máximo 10MB`
          continue
        }
        validFiles.push(file)
      }

      if (validFiles.length === 0) return

      validFiles.forEach(file => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          images.value.push({
            file: file,
            preview: base64,
            base64: base64
          })
        }
        reader.readAsDataURL(file)
      })

      if (fileInput.value) fileInput.value.value = ''
    }

    const removeImage = (index) => {
      images.value.splice(index, 1)
    }

    return {
      fileInput,
      caption,
      images,
      errorMessage,
      triggerFileInput,
      handleFileSelect,
      removeImage
    }
  }
}
</script>

<style scoped>
.upload-images-container {
  width: 100%;
}

.upload-area {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.upload-area:hover {
  border-color: #4299e1;
  background: rgba(66, 153, 225, 0.05);
}

.file-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  cursor: pointer;
  color: #718096;
}

.upload-text {
  font-size: 1.125rem;
  font-weight: 500;
  color: #4a5568;
  margin: 0.5rem 0;
}

.upload-hint {
  font-size: 0.875rem;
  color: #a0aec0;
  margin: 0;
}

.images-preview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.image-preview-item {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(229, 62, 62, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-image-btn:hover {
  background: #e53e3e;
  transform: scale(1.1);
}

.add-more-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  border: 2px dashed #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  color: #718096;
  transition: all 0.2s ease;
}

.add-more-btn:hover {
  border-color: #4299e1;
  background: rgba(66, 153, 225, 0.05);
  color: #4299e1;
}

.add-more-btn p {
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  font-weight: 500;
}

.error-message {
  padding: 0.75rem 1rem;
  background: rgba(229, 62, 62, 0.1);
  color: #e53e3e;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .images-preview {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.75rem;
  }

  .upload-area {
    padding: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/UploadImages.vue